import React, { useEffect, useState } from "react";
import { Calendar, MapPin, Clock } from "lucide-react";
import { collection, getDocs, query, orderBy, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import BtsoHaberSection from "../components/home/BtsoHaberSection";

interface Etkinlik {
  id: string;
  baslik: string;
  tarih: string;
  yer?: string;
  saat?: string;
  kategori?: string;
  aciklama?: string;
}

const categoryColors: Record<string, string> = {
  Kültür: "bg-brand-bordo text-white",
  Gençlik: "bg-brand-blue text-white",
  Aile: "bg-brand-green text-white",
  Genel: "bg-bg-soft text-text-muted",
};

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<Etkinlik[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [active, setActive] = useState("Tümü");

  // Kayıt modal'ı — artık etkinliğin Firestore döküman ID'si (string)
  const [registerEvent, setRegisterEvent] = useState<string | null>(null);
  const [regForm, setRegForm] = useState({ name: "", email: "", phone: "" });
  const [regSubmitted, setRegSubmitted] = useState(false);
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [regError, setRegError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setHata(false);
      try {
        const snap = await getDocs(query(collection(db, "etkinlikler"), orderBy("sira", "asc")));
        const list: Etkinlik[] = snap.docs.map((d) => {
          const data = d.data() as any;
          return {
            id: d.id,
            baslik: data.baslik ?? "",
            tarih: data.tarih ?? "",
            yer: data.yer ?? "",
            saat: data.saat ?? "",
            kategori: (data.kategori ?? "").trim() || "Genel", // boşsa Genel
            aciklama: data.aciklama ?? "",
          };
        });
        if (!cancelled) setEvents(list);
      } catch (e) {
        console.error("Etkinlikler okunamadı:", e);
        if (!cancelled) setHata(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filtre seçenekleri: "Tümü" + verideki mevcut kategoriler (sabit sırada).
  const KAT_SIRA = ["Kültür", "Gençlik", "Aile", "Genel"];
  const mevcutKategoriler = KAT_SIRA.filter((k) => events.some((e) => e.kategori === k));
  const categories = ["Tümü", ...mevcutKategoriler];

  const filtered = active === "Tümü" ? events : events.filter((e) => e.kategori === active);

  const aktifEtkinlik = events.find((e) => e.id === registerEvent);

  const handleReg = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError("");
    setRegSubmitting(true);
    try {
      const digits = regForm.phone.replace(/\D/g, "");
      await addDoc(collection(db, "etkinlikKayitlari"), {
        ad: regForm.name.trim(),
        email: regForm.email.trim(),
        telefon: digits ? "0" + (digits.startsWith("0") ? digits.slice(1) : digits) : "",
        etkinlikBaslik: aktifEtkinlik?.baslik || "",
        etkinlikId: registerEvent, // Firestore döküman ID (string)
        tarih: serverTimestamp(),
        durum: "bekliyor",
      });
      setRegSubmitted(true);
    } catch (err) {
      console.error("Etkinlik kaydı gönderilemedi:", err);
      setRegError("Kayıt gönderilemedi, lütfen tekrar deneyin.");
    } finally {
      setRegSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-page pt-24">
      <div className="bg-gradient-to-br from-[#0F172A] to-brand-blue-dark py-16 text-white">
        <div className="w-full px-6 xl:px-16 text-center">
          <div className="text-brand-gold text-sm font-semibold uppercase tracking-wider mb-2">
            Faaliyetler
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Etkinlik Takvimi</h1>
          <p className="text-white/70 max-w-xl mx-auto">
            Birlikte büyüyoruz. Birlikte hatırlıyoruz. Birlikte yürüyoruz.
          </p>
        </div>
      </div>

      <BtsoHaberSection />

      <div className="w-full px-6 xl:px-16 py-12">
        {loading ? (
          <div className="py-20 text-center text-text-soft">
            <i className="fa-solid fa-circle-notch fa-spin text-3xl text-brand-bordo" />
            <p className="mt-3 text-sm">Etkinlikler yükleniyor...</p>
          </div>
        ) : hata ? (
          <div className="py-20 text-center text-red-600">
            Etkinlikler yüklenemedi. Lütfen sayfayı yenileyin.
          </div>
        ) : events.length === 0 ? (
          <div className="py-20 text-center text-text-muted">
            <Calendar size={40} className="mx-auto mb-3 opacity-40" />
            <p className="font-medium">Henüz planlanmış etkinlik yok.</p>
            <p className="text-sm text-text-soft mt-1">Yeni etkinlikler yakında burada olacak.</p>
          </div>
        ) : (
          <>
            {/* Filter */}
            <div className="flex gap-2 flex-wrap mb-8">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActive(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    active === cat
                      ? "bg-brand-bordo text-white"
                      : "bg-bg-surface border border-border-light text-text-muted hover:text-text-primary"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Events grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((event) => (
                <div key={event.id} className="card flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        categoryColors[event.kategori || "Genel"] || "bg-bg-soft text-text-muted"
                      }`}
                    >
                      {event.kategori}
                    </span>
                  </div>
                  <h3 className="font-bold text-text-primary text-lg mb-2">{event.baslik}</h3>
                  {event.aciklama ? (
                    <p className="text-text-muted text-sm leading-relaxed flex-1 mb-4">{event.aciklama}</p>
                  ) : (
                    <div className="flex-1 mb-4" />
                  )}
                  <div className="space-y-1.5 pt-4 border-t border-border-light mb-4">
                    {event.tarih ? (
                      <div className="flex items-center gap-2 text-xs text-text-soft">
                        <Calendar size={13} />
                        {event.tarih}
                      </div>
                    ) : null}
                    {event.saat ? (
                      <div className="flex items-center gap-2 text-xs text-text-soft">
                        <Clock size={13} />
                        {event.saat}
                      </div>
                    ) : null}
                    {event.yer ? (
                      <div className="flex items-center gap-2 text-xs text-text-soft">
                        <MapPin size={13} />
                        {event.yer}
                      </div>
                    ) : null}
                  </div>
                  <button
                    onClick={() => {
                      setRegisterEvent(event.id);
                      setRegSubmitted(false);
                      setRegError("");
                      setRegForm({ name: "", email: "", phone: "" });
                    }}
                    className="btn-primary text-sm py-2.5 text-center"
                  >
                    Katıl
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Registration modal */}
      {registerEvent !== null && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setRegisterEvent(null)}
        >
          <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {regSubmitted ? (
              <div className="text-center py-6">
                <div className="text-4xl mb-3">✓</div>
                <h3 className="font-bold text-text-primary text-xl mb-2">Kaydınız Alındı!</h3>
                <p className="text-text-muted text-sm mb-4">
                  Etkinlik bilgileri e-posta ile iletilecektir.
                </p>
                <button onClick={() => setRegisterEvent(null)} className="btn-primary text-sm">
                  Kapat
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-bold text-text-primary text-lg mb-1">Etkinliğe Kayıt</h3>
                <p className="text-text-muted text-sm mb-5">{aktifEtkinlik?.baslik}</p>
                <form onSubmit={handleReg} className="space-y-3">
                  {[
                    { name: "name", label: "Adınız Soyadınız", type: "text", placeholder: "Ad Soyad" },
                    { name: "email", label: "E-posta", type: "email", placeholder: "ornek@email.com" },
                    { name: "phone", label: "Telefon", type: "tel", placeholder: "0555 000 00 00" },
                  ].map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-text-primary mb-1">{field.label}</label>
                      <input
                        type={field.type}
                        required={field.name !== "phone"}
                        value={(regForm as any)[field.name]}
                        onChange={(e) =>
                          setRegForm((prev) => ({ ...prev, [field.name]: e.target.value }))
                        }
                        className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-bordo"
                        placeholder={field.placeholder}
                      />
                    </div>
                  ))}
                  {regError && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                      {regError}
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setRegisterEvent(null)}
                      className="flex-1 border border-border-light rounded-md py-2.5 text-sm text-text-muted hover:bg-bg-soft"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={regSubmitting}
                      className="flex-1 btn-primary py-2.5 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {regSubmitting ? "Gönderiliyor..." : "Kaydol"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
